import {
    type Finra3210MimeType,
    type UserInvestmentProfile,
    YesNo,
} from '@fruitful/investment-management';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FileTextIcon,
    Input,
    isWeb,
    LabelRegular,
    SelectSingle,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { captureException } from '../../logger/Logger';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/tiff',
] as const;

export interface Finra3210Document {
    fileContentBase64: string;
    mimeType: Finra3210MimeType;
    name: string;
}

interface StockExchangeMemberStepProps {
    question: string;
    initialValue?: YesNo;
    firmName: string;
    document: Finra3210Document | null;
    isPending: boolean;
    onSelect: (updates: Partial<UserInvestmentProfile>) => Promise<void>;
    onFirmNameChange: (firmName: string) => void;
    onDocumentChange: (document: Finra3210Document | null) => void;
    onContinue: () => void;
}

const arrayBufferToBase64 = (buffer: ArrayBuffer): string =>
    btoa(
        Array.from(new Uint8Array(buffer), (byte) =>
            String.fromCharCode(byte),
        ).join(''),
    );

const getBase64ByteLength = (base64: string): number => {
    const paddingLength = base64.endsWith('==')
        ? 2
        : base64.endsWith('=')
          ? 1
          : 0;
    return Math.ceil((base64.length * 3) / 4) - paddingLength;
};

const getMimeType = (
    asset: DocumentPicker.DocumentPickerAsset,
): Finra3210MimeType | null => {
    const mimeType = asset.mimeType?.toLowerCase();
    if (
        mimeType != null &&
        (ALLOWED_MIME_TYPES as readonly string[]).includes(mimeType)
    ) {
        return mimeType as Finra3210MimeType;
    }

    const extension = asset.name.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'pdf':
            return 'application/pdf';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        case 'tif':
        case 'tiff':
            return 'image/tiff';
        default:
            return null;
    }
};

const readFileAsBase64 = async (uri: string): Promise<string> => {
    if (!isWeb) {
        return await FileSystem.readAsStringAsync(uri, {
            encoding: FileSystem.EncodingType.Base64,
        });
    }

    const response = await fetch(uri);
    if (!response.ok) {
        throw new Error(`Unable to read selected file: ${response.status}`);
    }
    return arrayBufferToBase64(await response.arrayBuffer());
};

export const StockExchangeMemberStep = ({
    question,
    initialValue,
    firmName,
    document,
    isPending,
    onSelect,
    onFirmNameChange,
    onDocumentChange,
    onContinue,
}: StockExchangeMemberStepProps) => {
    const [selectedValue, setSelectedValue] = useState<YesNo | undefined>(
        initialValue,
    );
    const [fileError, setFileError] = useState<string | null>(null);
    const [isReadingFile, setIsReadingFile] = useState(false);
    const optionValues = [YesNo.YES, YesNo.NO];

    const selectDocument = async () => {
        setFileError(null);
        const result = await DocumentPicker.getDocumentAsync({
            type: [...ALLOWED_MIME_TYPES],
            copyToCacheDirectory: true,
            multiple: false,
        });
        if (result.canceled) return;

        const asset = result.assets[0];
        const mimeType = asset != null ? getMimeType(asset) : null;
        if (asset == null || mimeType == null) {
            setFileError('Choose a PDF, JPEG, PNG, or TIFF file.');
            return;
        }
        if (asset.size != null && asset.size > MAX_FILE_SIZE_BYTES) {
            setFileError('The file must be 10 MiB or smaller.');
            return;
        }

        setIsReadingFile(true);
        try {
            const fileContentBase64 = await readFileAsBase64(asset.uri);
            if (getBase64ByteLength(fileContentBase64) > MAX_FILE_SIZE_BYTES) {
                setFileError('The file must be 10 MiB or smaller.');
                return;
            }
            onDocumentChange({
                fileContentBase64,
                mimeType,
                name: asset.name,
            });
        } catch (error) {
            captureException(error);
            setFileError('We could not attach that file. Please try again.');
        } finally {
            setIsReadingFile(false);
        }
    };

    const canContinue =
        firmName.trim() !== '' && document != null && !isReadingFile;

    return (
        <YStack flex={1} gap={16} paddingVertical={16}>
            <BodyMediumRegular>{question}</BodyMediumRegular>
            <SelectSingle
                options={[{ title: 'Yes' }, { title: 'No' }]}
                selectedIndex={
                    selectedValue != null
                        ? optionValues.indexOf(selectedValue)
                        : null
                }
                onSelect={(selectedIndex) => {
                    const value = optionValues[selectedIndex];
                    setSelectedValue(value);
                    if (value === YesNo.NO) {
                        onFirmNameChange('');
                        onDocumentChange(null);
                    }
                    void onSelect({ stockExchangeMember: value });
                }}
            />
            {selectedValue === YesNo.YES && (
                <>
                    {/* Prevent dynamic border width changes on focus from clipping */}
                    <YStack gap={16} paddingHorizontal={2}>
                        <YStack gap={8}>
                            <LabelRegular>Firm name</LabelRegular>
                            <Input
                                value={firmName}
                                placeholder="Enter firm name"
                                onChangeText={onFirmNameChange}
                            />
                        </YStack>
                        <YStack gap={8}>
                            <LabelRegular>FINRA 3210 letter</LabelRegular>
                            <Button
                                text={
                                    isReadingFile
                                        ? 'Attaching...'
                                        : document == null
                                          ? 'Attach letter'
                                          : 'Replace letter'
                                }
                                icon={
                                    isReadingFile ? <Spinner /> : FileTextIcon
                                }
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.LARGE}
                                disabled={isReadingFile}
                                onPress={() => {
                                    void selectDocument();
                                }}
                            />
                            {document != null && (
                                <XStack gap={8} alignItems="center">
                                    <BodySmallRegular color="$gray1000">
                                        {document.name}
                                    </BodySmallRegular>
                                </XStack>
                            )}
                            {fileError != null && (
                                <BodySmallRegular color="$red1000">
                                    {fileError}
                                </BodySmallRegular>
                            )}
                            <BodySmallRegular color="$gray1000">
                                PDF, JPEG, PNG, or TIFF. Maximum 10 MiB.
                            </BodySmallRegular>
                        </YStack>
                    </YStack>
                    <YStack marginTop="auto" gap={16} paddingHorizontal={2}>
                        <BodySmallRegular color="$gray1000">
                            By submitting this document I certify that it is
                            authentic and that the information provided is true,
                            accurate, complete, and current, with no material
                            information omitted or misrepresented.
                        </BodySmallRegular>
                        <Button
                            text="Continue"
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            disabled={!canContinue || isPending}
                            onPress={onContinue}
                        />
                    </YStack>
                </>
            )}
        </YStack>
    );
};
